import { NavLink } from "react-router";
import { motion } from "motion/react";
import {
  BrainCircuit,
  ArrowRight,
  CheckCircle2,
  Compass,
  RotateCcw,
  Sparkles,
  BookOpen,
  Briefcase,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import {
  RIASEC_TYPES,
  RIASEC_FIELDS_MAP,
  RiasecCode,
} from "../../Data/mockDataCareerTest";

function EmptyState() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="flex flex-col items-center justify-center text-center py-20 px-6 max-w-sm mx-auto"
    >
      <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-emerald-500 to-teal-400 flex items-center justify-center mb-5 shadow-xl shadow-emerald-500/20">
        <BrainCircuit className="w-9 h-9 text-white" />
      </div>
      <h2
        className="text-[22px] text-slate-900 mb-2 tracking-tight"
        style={{ fontWeight: 800 }}
      >
        No Results Yet
      </h2>
      <p
        className="text-[14px] text-slate-500 leading-relaxed mb-7"
        style={{ fontWeight: 400 }}
      >
        Take the 5-minute RIASEC Career Personality Test to discover which
        university fields and career paths suit you best.
      </p>
      <NavLink
        to="/dashboard/career-test"
        className="flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-[14px] shadow-md hover:-translate-y-0.5 transition-all"
        style={{ fontWeight: 700 }}
      >
        Start Career Test <ArrowRight className="w-4 h-4" />
      </NavLink>
    </motion.div>
  );
}

export default function CareerTestResultPage() {
  const { user } = useAuth();
  const result = user?.careerTestResult;

  if (!result)
    return (
      <div className="p-4 lg:p-8 max-w-3xl mx-auto">
        <div className="mb-6">
          <h1
            className="text-[24px] lg:text-[30px] text-slate-900 tracking-tight"
            style={{ fontWeight: 900 }}
          >
            Career Test
          </h1>
          <p
            className="text-[13px] text-slate-500 mt-1"
            style={{ fontWeight: 400 }}
          >
            Your RIASEC career personality results.
          </p>
        </div>
        <EmptyState />
      </div>
    );

  const topTypes = result.topTypes as RiasecCode[];
  const scores = result.scores as Record<RiasecCode, number>;
  const primaryType = RIASEC_TYPES.find((t) => t.code === topTypes[0])!;
  const topTypeMeta = topTypes.map(
    (code) => RIASEC_TYPES.find((t) => t.code === code)!,
  );
  const codeLabel = topTypes.join("");

  const allFields = [
    ...new Set(topTypes.flatMap((c) => RIASEC_FIELDS_MAP[c].fields)),
  ].slice(0, 9);
  const allCourses = [
    ...new Set(topTypes.flatMap((c) => RIASEC_FIELDS_MAP[c].courses)),
  ].slice(0, 6);
  const allCareers = [
    ...new Set(topTypes.flatMap((c) => RIASEC_FIELDS_MAP[c].careers)),
  ].slice(0, 6);

  const completedDate = new Date(result.completedAt).toLocaleDateString(
    "en-MY",
    {
      day: "numeric",
      month: "long",
      year: "numeric",
    },
  );

  return (
    <div className="p-4 lg:p-8 max-w-3xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
        className="mb-6"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1
              className="text-[24px] lg:text-[30px] text-slate-900 tracking-tight"
              style={{ fontWeight: 900 }}
            >
              Career Test
            </h1>
            <p
              className="text-[13px] text-slate-500 mt-1"
              style={{ fontWeight: 400 }}
            >
              Completed on {completedDate} · Based on Holland's RIASEC model
            </p>
          </div>
          <NavLink
            to="/dashboard/career-test"
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 text-slate-600 text-[13px] hover:bg-slate-50 transition-colors"
            style={{ fontWeight: 600 }}
          >
            <RotateCcw className="w-3.5 h-3.5" /> Retake Test
          </NavLink>
        </div>
      </motion.div>

      {/* Hero banner */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, delay: 0.06 }}
        className={`bg-gradient-to-br ${primaryType.bg} rounded-3xl p-7 text-white text-center mb-6 relative overflow-hidden`}
      >
        <div className="absolute top-0 right-0 w-48 h-48 rounded-full bg-white/10 -translate-y-1/3 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-32 h-32 rounded-full bg-white/5 translate-y-1/2 -translate-x-1/4" />
        <div className="relative z-10">
          <p
            className="text-[12px] text-white/70 uppercase tracking-widest mb-2"
            style={{ fontWeight: 700 }}
          >
            Your Career Code
          </p>
          <p
            className="text-[52px] leading-none mb-2"
            style={{ fontWeight: 900 }}
          >
            {codeLabel}
          </p>
          <p
            className="text-[17px] text-white/90 mb-0.5"
            style={{ fontWeight: 700 }}
          >
            {topTypeMeta.map((t) => t.label).join(" · ")}
          </p>
          <p className="text-[13px] text-white/60" style={{ fontWeight: 400 }}>
            {topTypeMeta.map((t) => t.nickname).join(" · ")}
          </p>
        </div>
      </motion.div>

      {/* Type breakdown */}
      <div className="flex flex-col gap-4 mb-6">
        {topTypeMeta.map((type, i) => {
          const score = scores[type.code] ?? 0;
          const pct = Math.round((score / 10) * 100);
          return (
            <motion.div
              key={type.code}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.12 + i * 0.07 }}
              className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5"
            >
              <div className="flex items-center gap-3 mb-3">
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center text-[20px] flex-shrink-0"
                  style={{ backgroundColor: `${type.color}18` }}
                >
                  {type.emoji}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <p
                      className="text-[14px] text-slate-900"
                      style={{ fontWeight: 700 }}
                    >
                      {type.label}
                    </p>
                    <span
                      className="text-[10px] px-2 py-0.5 rounded-full text-white"
                      style={{ backgroundColor: type.color, fontWeight: 700 }}
                    >
                      {type.nickname}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${pct}%` }}
                        transition={{ duration: 0.8, delay: 0.2 + i * 0.1 }}
                        className="h-full rounded-full"
                        style={{ backgroundColor: type.color }}
                      />
                    </div>
                    <span
                      className="text-[11px] text-slate-500 flex-shrink-0"
                      style={{ fontWeight: 600 }}
                    >
                      {score}/10
                    </span>
                  </div>
                </div>
              </div>
              <p
                className="text-[13px] text-slate-500 leading-relaxed"
                style={{ fontWeight: 400 }}
              >
                {type.description}
              </p>
            </motion.div>
          );
        })}
      </div>

      {/* Full scores — all 6 types */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.32 }}
        className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 mb-6"
      >
        <h3
          className="text-[14px] text-slate-900 mb-4"
          style={{ fontWeight: 700 }}
        >
          Full Score Breakdown
        </h3>
        <div className="flex flex-col gap-3">
          {RIASEC_TYPES.map((type, i) => {
            const score = scores[type.code] ?? 0;
            const pct = Math.round((score / 10) * 100);
            const isTop = topTypes.includes(type.code);
            return (
              <div key={type.code} className="flex items-center gap-3">
                <span className="w-6 text-[16px] flex-shrink-0">
                  {type.emoji}
                </span>
                <span
                  className={`w-24 text-[12px] flex-shrink-0 ${isTop ? "text-slate-900" : "text-slate-500"}`}
                  style={{ fontWeight: isTop ? 700 : 400 }}
                >
                  {type.label}
                </span>
                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ duration: 0.7, delay: 0.4 + i * 0.05 }}
                    className="h-full rounded-full"
                    style={{ backgroundColor: isTop ? type.color : "#CBD5E1" }}
                  />
                </div>
                <span
                  className="text-[12px] text-slate-500 flex-shrink-0 w-8 text-right"
                  style={{ fontWeight: 600 }}
                >
                  {score}
                </span>
              </div>
            );
          })}
        </div>
      </motion.div>

      {/* Fields of study */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.38 }}
        className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 mb-4"
      >
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-violet-500" />
          <h3
            className="text-[14px] text-slate-900"
            style={{ fontWeight: 700 }}
          >
            Recommended Fields of Study
          </h3>
        </div>
        <div className="flex flex-wrap gap-2">
          {allFields.map((field, i) => (
            <span
              key={i}
              className="px-3 py-1.5 rounded-full bg-violet-50 border border-violet-100 text-[12px] text-violet-700"
              style={{ fontWeight: 600 }}
            >
              {field}
            </span>
          ))}
        </div>
      </motion.div>

      {/* Courses + careers */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.42 }}
        className="grid sm:grid-cols-2 gap-4 mb-6"
      >
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="w-4 h-4 text-blue-500" />
            <h3
              className="text-[14px] text-slate-900"
              style={{ fontWeight: 700 }}
            >
              Example Courses
            </h3>
          </div>
          <div className="flex flex-col gap-2">
            {allCourses.map((c, i) => (
              <div key={i} className="flex items-start gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-violet-500 flex-shrink-0 mt-0.5" />
                <span
                  className="text-[12px] text-slate-600"
                  style={{ fontWeight: 400 }}
                >
                  {c}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-3">
            <Briefcase className="w-4 h-4 text-emerald-500" />
            <h3
              className="text-[14px] text-slate-900"
              style={{ fontWeight: 700 }}
            >
              Career Paths
            </h3>
          </div>
          <div className="flex flex-col gap-2">
            {allCareers.map((c, i) => (
              <div key={i} className="flex items-start gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                <span
                  className="text-[12px] text-slate-600"
                  style={{ fontWeight: 400 }}
                >
                  {c}
                </span>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* CTAs */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.46 }}
        className="flex flex-col sm:flex-row gap-3"
      >
        <NavLink
          to="/course-finder"
          className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 text-white text-[14px] shadow-md hover:-translate-y-0.5 transition-all"
          style={{ fontWeight: 700 }}
        >
          <Compass className="w-4 h-4" /> Explore Course Finder
        </NavLink>
        <NavLink
          to="/dashboard/chat"
          className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl border border-slate-200 text-slate-700 text-[14px] hover:bg-slate-50 transition-all"
          style={{ fontWeight: 600 }}
        >
          <BrainCircuit className="w-4 h-4 text-emerald-500" /> Discuss with
          Counsellor
        </NavLink>
      </motion.div>
    </div>
  );
}
